import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useResizeEventListener } from '@vista-digital/react-tools';
import Cropper from 'cropperjs';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ImageCropperControls } from './subcomponents/controls';
const b = bemClassBuilder('v-image-cropper');
const maxZoomRatio = 2;
const ImageCropperBase = (props, ref) => {
    const { imageString, rotateButtonIconName, rotateButtonLabel, zoomInButtonIconName, zoomOutButtonIconName } = props;
    const [zoomRatio, setZoomRatio] = useState(0);
    const [minZoomRatio, setMinZoomRatio] = useState(0.05);
    const imgRef = useRef(null);
    const cropper = useRef();
    useResizeEventListener(() => {
        var _a;
        // Cropperjs restores the original image when resizing the window, which resets the zoom, so we have to re-zoom in
        (_a = cropper.current) === null || _a === void 0 ? void 0 : _a.zoomTo(zoomRatio);
    });
    useEffect(() => {
        if (!imgRef.current)
            return;
        if (!cropper.current) {
            cropper.current = new Cropper(imgRef.current, {
                viewMode: 1,
                dragMode: 'move',
                guides: true,
                cropBoxMovable: false,
                cropBoxResizable: false,
                background: false,
                minCropBoxWidth: 200,
                minCropBoxHeight: 200,
                center: false,
                responsive: true,
                restore: false,
                aspectRatio: 1,
                zoom: e => {
                    // We need to keep within the min / max zoom bounds.
                    if (e.detail.ratio > maxZoomRatio) {
                        e.preventDefault();
                        cropper.current.zoomTo(maxZoomRatio);
                        setZoomRatio(maxZoomRatio);
                    }
                    else if (e.detail.ratio < minZoomRatio) {
                        e.preventDefault();
                        cropper.current.zoomTo(minZoomRatio);
                        setZoomRatio(minZoomRatio);
                    }
                    else {
                        setZoomRatio(e.detail.ratio);
                    }
                },
                ready: () => {
                    const canvasData = cropper.current.getCanvasData();
                    const currentZoomRatio = canvasData.width / canvasData.naturalWidth;
                    const cropBoxData = cropper.current.getCropBoxData();
                    // Since the cropbox is the same size as either the height or width, we are currently at the min ratio
                    if (cropBoxData.width === Math.min(canvasData.height, canvasData.width)) {
                        setMinZoomRatio(currentZoomRatio);
                        setZoomRatio(currentZoomRatio);
                        return;
                    }
                    // Scale down the image to the same size as the cropbox (while maintaining aspect ratio)
                    const imageRatio = Math.max(cropBoxData.width / canvasData.width, cropBoxData.height / canvasData.height);
                    const scaledWidth = canvasData.width * imageRatio;
                    setMinZoomRatio(scaledWidth / canvasData.naturalWidth);
                    setZoomRatio(currentZoomRatio);
                }
            });
        }
        cropper.current.replace(imageString);
        return () => {
            if (cropper.current)
                cropper.current.destroy();
        };
    }, [imgRef, imageString, setZoomRatio, minZoomRatio]);
    useImperativeHandle(ref, () => ({
        getCroppedImageString: () => {
            if (!cropper.current)
                throw new Error("Cropper instance doesn't exist.");
            return cropper.current.getCroppedCanvas().toDataURL();
        }
    }));
    const handleRotate = (degrees) => {
        cropper.current && cropper.current.rotate(degrees);
    };
    const handleZoomRatioChanged = (ratio) => {
        cropper.current && cropper.current.zoomTo(ratio);
    };
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [_jsx("div", Object.assign({ className: b.element('image') }, { children: _jsx("img", { className: b.element('img'), ref: imgRef }) })), _jsx(ImageCropperControls, { currentZoomRatio: zoomRatio, minZoomRatio: minZoomRatio, maxZoomRatio: maxZoomRatio, rotateButtonIconName: rotateButtonIconName, rotateButtonLabel: rotateButtonLabel, zoomInButtonIconName: zoomInButtonIconName, zoomOutButtonIconName: zoomOutButtonIconName, onRotate: handleRotate, onZoomRatioChanged: handleZoomRatioChanged })] })));
};
export const ImageCropper = forwardRef(ImageCropperBase);
ImageCropper.displayName = 'ImageCropper';
